<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Upload Free Chips</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label for="fileUpload">Select CSV file</label>
              <div class="input-group">
                <div class="custom-file">
                  <input
                    type="file"
                    class="custom-file-input"
                    id="fileUpload"
                    ref="fileInput"
                    accept=".csv"
                    @change="handleFileUpload"
                  />
                  <label class="custom-file-label" for="fileUpload">
                    {{ fileName || 'Choose file' }}
                  </label>
                </div>
                <div class="input-group-append">
                  <button
                    class="btn btn-primary"
                    type="button"
                    @click="uploadFile"
                    :disabled="!selectedFile || isUploading"
                  >
                    <i class="fas fa-upload mr-1"></i>
                    {{ isUploading ? 'Uploading...' : 'Upload' }}
                  </button>
                </div>
              </div>
              <small class="form-text text-muted mt-2">
                Please upload a CSV file with the format:
                amount;token;gameId;uid;expiresAt;currency (expiresAt in
                minutes)
              </small>
            </div>

            <div
              v-if="uploadSuccess"
              class="alert"
              :class="{
                'alert-success': uploadDetails.failed_uploads === 0,
                'alert-warning': uploadDetails.failed_uploads > 0
              }"
            >
              <h5>
                <i
                  class="icon fas"
                  :class="{
                    'fa-check': uploadDetails.failed_uploads === 0,
                    'fa-exclamation-triangle': uploadDetails.failed_uploads > 0
                  }"
                ></i>
                {{
                  uploadDetails.failed_uploads === 0
                    ? 'Success!'
                    : 'Completed with warnings'
                }}
              </h5>
              <div v-if="uploadDetails">
                <p class="mb-2"><strong>Summary:</strong></p>
                <ul class="mb-3">
                  <li>Total lines: {{ uploadDetails.total_lines }}</li>
                  <li>
                    Processed entries: {{ uploadDetails.processed_lines }}
                  </li>
                  <li>Skipped lines: {{ uploadDetails.skipped_lines }}</li>
                  <li>
                    Successful uploads:
                    <span class="text-success">{{
                      uploadDetails.successful_uploads
                    }}</span>
                  </li>
                  <li>
                    Failed uploads:
                    <span
                      :class="{
                        'text-danger': uploadDetails.failed_uploads > 0
                      }"
                      >{{ uploadDetails.failed_uploads }}</span
                    >
                  </li>
                  <li>
                    Header detected:
                    {{ uploadDetails.had_header ? 'Yes' : 'No' }}
                  </li>
                </ul>
              </div>

              <div
                v-if="
                  uploadDetails &&
                    uploadDetails.errors &&
                    uploadDetails.errors.length > 0
                "
              >
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <h6 class="mb-0"><strong>Error Details:</strong></h6>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="showErrors = !showErrors"
                  >
                    {{ showErrors ? 'Hide' : 'Show' }} Errors
                  </button>
                </div>

                <div v-if="showErrors" class="table-responsive">
                  <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                      <tr>
                        <th>Line</th>
                        <th>Error</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(error, index) in uploadDetails.errors"
                        :key="index"
                      >
                        <td>{{ error.line }}</td>
                        <td>{{ error.error }}</td>
                        <td>
                          <pre class="mb-0 small">{{
                            JSON.stringify(error.data, null, 2)
                          }}</pre>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div v-if="uploadError" class="alert alert-danger mt-3">
              <h5><i class="icon fas fa-ban"></i> Error!</h5>
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'UploadFreeChips',
  data() {
    return {
      selectedFile: null,
      fileName: '',
      isUploading: false,
      uploadSuccess: false,
      uploadError: false,
      errorMessage: '',
      uploadDetails: null,
      showErrors: false
    }
  },
  methods: {
    handleFileUpload(event) {
      this.selectedFile = event.target.files[0]
      this.fileName = this.selectedFile ? this.selectedFile.name : ''
      this.uploadSuccess = false
      this.uploadError = false
      this.uploadDetails = null
      this.showErrors = false
    },
    async uploadFile() {
      if (!this.selectedFile) return

      this.isUploading = true
      this.uploadSuccess = false
      this.uploadError = false
      this.uploadDetails = null
      this.showErrors = false

      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile)

        const response = await axios.post(
          '/api/v1/upload-free-chips',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )

        if (response.data.status === 'ok') {
          this.uploadSuccess = true
          this.uploadDetails = response.data.details

          // Automatically show errors if there are only a few
          if (
            this.uploadDetails.errors &&
            this.uploadDetails.errors.length <= 5
          ) {
            this.showErrors = true
          }

          this.selectedFile = null
          this.fileName = ''

          // Reset file input to allow selecting the same file again
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
        } else {
          throw new Error(response.data.message || 'Failed to upload file')
        }
      } catch (error) {
        this.uploadError = true
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Failed to upload file. Please try again.'
      } finally {
        this.isUploading = false
      }
    }
  }
}
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 100px;
  overflow-y: auto;
}
</style>
